// SEQ-199: the grid widget body (the Profit Pareto table and any other
// rows-by-months table built from the transformation library).
//
// This file only DRAWS. Every decision (columns, order, blanks, bands, Safe
// Mode masking, notes) comes from gridModel.ts, which a deno test proves, and
// the model hands this component display text only, so a figure cannot reach
// the page except through the masked formatter.
//
// DATA. Totals come from the widget's own project-grain request key (the
// dashboard's shared planner, `view`), the month cells from one extra
// `project_month` call for the same range and filters, exactly as the Project
// performance table's By month view does it (usePnlProjectMonths).
//
// CONTROLS. Sortable headers (buttons, aria-sort), a column picker, full
// screen (the page's Modal, so Escape and focus return behave like every other
// dialog), CSV and XLSX through the same helpers as the Project performance
// table, disabled while Safe Mode is on.
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import * as XLSX from "xlsx";
import { AlertTriangle, ArrowDown, ArrowUp, ArrowUpDown, Download, Maximize2, RefreshCw, Settings2 } from "lucide-react";
import type { PnlWidgetConfig } from "../../lib/pnl/pnlWidgetSchema";
import { resolveGridConfig, type PnlGridSort } from "../../lib/pnl/pnlGridConfig";
import { isSafeModeActive } from "../../lib/calculations";
import type { PnlKeyView } from "../../features/pnlAnalytics/widgetDataset";
import { resolveWidgetFilters, type PnlDashboardPageFilters } from "../../features/pnlAnalytics/dashboardRequestKeys";
import type { PnlAnalyticsQueryFilters } from "../../features/pnlAnalytics/usePnlAnalytics";
import { usePnlProjectMonths } from "../../features/pnlAnalytics/usePnlProjectMonths";
import { buildMonthOnMonthTable } from "../../features/pnlAnalytics/monthOnMonth";
import { applyGridCosts } from "../../features/pnlAnalytics/gridCosts";
import { staleGridInput } from "../../features/pnlAnalytics/gridStale";
import { gridErrorKind, gridLoadErrorCopy, gridRefreshErrorCopy } from "../../features/pnlAnalytics/gridErrorCopy";
import { metricsToRollup, type PnlRollup } from "../../features/pnlAnalytics/rollup";
import { useManualCostMonths } from "../../features/pnlAnalytics/useManualCostMonths";
import {
  COSTS_CHECK_FAILED_NOTE,
  fullyUnfinishedMonths,
  loadShowNoCostMonths,
  noCostKeys,
  saveShowNoCostMonths,
  SHOW_NO_COST_MONTHS_HINT,
  SHOW_NO_COST_MONTHS_LABEL,
  trustedCheck,
} from "../../features/pnlAnalytics/projectMonthCosts";
import { projectMonthRangeResult, type ProjectMonthRangeIssue } from "../../features/pnlAnalytics/projectMonthView";
import {
  buildGridModel,
  gridExportRecords,
  nextSort,
  NAME_COLUMN_ID,
  type GridColumn,
  type GridModel,
  type GridModelInput,
  type GridRow,
} from "../../features/pnlAnalytics/gridModel";
import { csvCell, xlsxCell } from "../../features/pnlAnalytics/spreadsheetSafety";
import { exportConfirmation, exportFilename, exportHeader, exportNumber } from "../../features/pnlAnalytics/tableExport";
import { Modal, useToast } from "../ui/ToastModal";
import { HowWorkedOut } from "../howWorkedOut/HowWorkedOut";
import { HowWorkedOutVisibility } from "../howWorkedOut/howWorkedOutVisibility";
import { gridColumnFigure, gridColumnTargets } from "../../features/pnlAnalytics/gridDescriptions";
import "./PnlWidgetCharts.css";

export interface PnlGridWidgetProps {
  config: PnlWidgetConfig;
  view: PnlKeyView;
  /** The page's live filter bar. The widget's own filters narrow it, never widen it (resolveWidgetFilters). */
  queryFilters: PnlAnalyticsQueryFilters;
  /** Bumped by the page Refresh. The grid reloads its own project_month call when it changes. */
  refreshTick?: number;
  /** SEQ-240: dataset.meta.payrollAsAt, for the Last updated row of the overtime columns. */
  payrollAsAt?: string | null;
}

const RANGE_MESSAGES: Record<ProjectMonthRangeIssue, string> = {
  open: "The grid needs a date range with a start and an end. Choose one in the filters above.",
  invalid: "The grid could not read that date range. Choose one in the filters above.",
  "too-long": "The grid shows at most 24 months. Choose a shorter range in the filters above.",
  "no-whole-month": "Pick a range that covers at least one whole month.",
};

const SAFE_MODE_EXPORT_HINT = "Exports are off while Safe Mode is on";

/** Today as 'YYYY-MM-DD' in the viewer's own calendar (local parts: toISOString is UTC and a day out in Brisbane mornings). */
function localTodayIso(): string {
  return format(new Date(), "yyyy-MM-dd");
}

/**
 * The inner widget is keyed by what the editor can change (saved sort, saved
 * hidden columns, widget id), so editing those in the drawer resets the
 * viewer's own sort and column choices to the new saved ones without an effect.
 */
export function PnlGridWidget(props: PnlGridWidgetProps) {
  const grid = props.config.grid;
  const signature = JSON.stringify([props.config.widgetId, grid?.sort ?? null, grid?.hiddenColumns ?? null, grid?.summaryMeasure ?? null, grid?.measures ?? null]);
  return <PnlGridWidgetInner key={signature} {...props} />;
}

function PnlGridWidgetInner({ config, view, queryFilters, refreshTick = 0, payrollAsAt }: PnlGridWidgetProps) {
  const { addToast } = useToast();
  const resolved = useMemo(() => resolveGridConfig(config.grid), [config.grid]);
  const [sort, setSort] = useState<PnlGridSort>(resolved.sort);
  const [hidden, setHidden] = useState<string[]>(resolved.hiddenColumns);
  const [columnMenuOpen, setColumnMenuOpen] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);
  // Read the same way formatCurrency does: the page is mounted below SafeModeProvider, which re-renders the subtree on toggle.
  const safeModeActive = isSafeModeActive();

  const page: PnlDashboardPageFilters = useMemo(
    () => ({ range: { from: queryFilters.from, to: queryFilters.to }, projectIds: queryFilters.projectIds, statuses: queryFilters.statuses }),
    [queryFilters.from, queryFilters.to, queryFilters.projectIds, queryFilters.statuses],
  );
  const resolvedFilters = useMemo(() => resolveWidgetFilters(config, page), [config, page]);

  const rangeResult = useMemo(() => projectMonthRangeResult(view.from, view.to), [view.from, view.to]);
  const months = rangeResult.months;
  const monthsEnabled = view.loaded && months !== null;
  const monthFilters = useMemo<PnlAnalyticsQueryFilters>(
    () => ({ from: view.from, to: view.to, projectIds: resolvedFilters.projectIds, statuses: resolvedFilters.statuses }),
    [view.from, view.to, resolvedFilters.projectIds, resolvedFilters.statuses],
  );
  const monthData = usePnlProjectMonths(monthFilters, monthsEnabled);
  // The page Refresh reloads this widget's own call too, so its month cells never disagree with a refreshed Total.
  const refreshMonths = monthData.refresh;
  const lastTick = useRef(refreshTick);
  useEffect(() => {
    if (refreshTick === lastTick.current) return;
    lastTick.current = refreshTick;
    refreshMonths();
  }, [refreshTick, refreshMonths]);
  const monthResponse = monthData.data;
  const monthCurrent = monthData.current;

  const todayIso = useMemo(() => localTodayIso(), []);
  const partialMonths = useMemo(() => {
    if (months === null || view.to === null) return [];
    return buildMonthOnMonthTable(months, [], false, { rangeToIso: view.to, todayIso })
      .columns.filter((c) => c.partial)
      .map((c) => c.periodMonth);
  }, [months, view.to, todayIso]);

  // SEQ-232: the same rule as the By month table, with the same helpers. A project month with income and no
  // manual costs yet is left out of the figures (and the Total) unless "Show months without costs" is on.
  const [showNoCostMonths, setShowNoCostMonths] = useState<boolean>(loadShowNoCostMonths);
  const rowsByProject = useMemo(() => {
    const byProject = new Map<string, Map<string, PnlRollup>>();
    if (!monthsEnabled || !monthResponse || !monthCurrent) return byProject;
    for (const r of monthResponse.rows) {
      if (!r.projectId || !r.periodMonth) continue;
      let perMonth = byProject.get(r.projectId);
      if (!perMonth) {
        perMonth = new Map();
        byProject.set(r.projectId, perMonth);
      }
      perMonth.set(r.periodMonth, metricsToRollup(r.metrics));
    }
    return byProject;
  }, [monthsEnabled, monthResponse, monthCurrent]);
  const costProjectIds = useMemo(() => [...rowsByProject.keys()], [rowsByProject]);
  const costCheck = useManualCostMonths(costProjectIds, months, monthsEnabled && monthCurrent);
  const flaggedKeys = useMemo(() => noCostKeys(rowsByProject, costCheck.check), [rowsByProject, costCheck.check]);
  const costCheckFailed =
    monthsEnabled && monthCurrent && costCheck.settled && rowsByProject.size > 0 &&
    trustedCheck(rowsByProject, costCheck.check).status === "unavailable";
  // With the switch off, a month where every project is still waiting for costs is not a column at all.
  const shownMonths = useMemo(() => {
    if (months === null) return null;
    if (showNoCostMonths || flaggedKeys.size === 0) return months;
    const dropped = fullyUnfinishedMonths(months, rowsByProject, flaggedKeys);
    return months.filter((m) => !dropped.has(m));
  }, [months, showNoCostMonths, flaggedKeys, rowsByProject]);

  const modelInput: GridModelInput | null = useMemo(() => {
    if (months === null || shownMonths === null || !monthResponse || !monthCurrent || !costCheck.settled) return null;
    // Marks the flagged project months and, with the switch off, takes them back out of each project's Total.
    const { projects, noCostMonths } = applyGridCosts(
      months,
      monthResponse.rows,
      view.projectRows.map((p) => ({ projectId: p.projectId, projectName: p.projectName, status: p.status, total: p })),
      flaggedKeys,
      showNoCostMonths,
    );
    return {
      months: shownMonths,
      monthRows: monthResponse.rows,
      projects,
      config: resolved,
      sort,
      hiddenColumns: hidden,
      partialMonths,
      noCostMonths,
      showNoCostMonths,
    };
  }, [months, shownMonths, monthResponse, monthCurrent, costCheck.settled, flaggedKeys, showNoCostMonths, view.projectRows, resolved, sort, hidden, partialMonths]);

  // Rebuilt on a Safe Mode toggle too: isSafeModeActive is read inside the model, and `safeModeActive` is in the dependency list for that reason.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- safeModeActive is the trigger; the model reads the flag itself
  const model: GridModel | null = useMemo(() => (modelInput ? buildGridModel(modelInput) : null), [modelInput, safeModeActive]);

  // When the connection comes back after a failed load, try again without waiting for a click.
  const retryAfterError = monthData.error ? monthData.refresh : null;
  useEffect(() => {
    if (!retryAfterError) return;
    window.addEventListener("online", retryAfterError);
    return () => window.removeEventListener("online", retryAfterError);
  }, [retryAfterError]);

  // The column menu closes on Escape (focus returns to its button) and on a click outside it.
  const menuRef = useRef<HTMLDivElement>(null);
  const columnsButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!columnMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setColumnMenuOpen(false);
      columnsButtonRef.current?.focus();
    };
    const onPointer = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setColumnMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [columnMenuOpen]);

  // A failed refresh keeps the last table that loaded and says so in a banner, instead of wiping what the viewer was reading.
  //
  // What is kept is the last good model INPUT with the request it was built for, never the display text: the stale
  // table is rebuilt at render, so Safe Mode masking is always current, and it is shown only while the range,
  // filters and months-without-costs choice still match the request it came from (otherwise old figures would sit
  // under new filter labels). It also covers the wait while Retry is loading, so the table does not flicker away.
  const requestKey = useMemo(
    () => JSON.stringify([view.from, view.to, resolvedFilters.projectIds, resolvedFilters.statuses, showNoCostMonths]),
    [view.from, view.to, resolvedFilters.projectIds, resolvedFilters.statuses, showNoCostMonths],
  );
  const [lastGood, setLastGood] = useState<{ key: string; input: GridModelInput } | null>(null);
  useEffect(() => {
    if (modelInput) setLastGood({ key: requestKey, input: modelInput });
  }, [modelInput, requestKey]);
  const staleInput = staleGridInput(lastGood, requestKey, modelInput !== null, Boolean(monthData.error) || monthData.loading);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- safeModeActive is the trigger; the model reads the flag itself
  const staleModel: GridModel | null = useMemo(() => (staleInput ? buildGridModel(staleInput) : null), [staleInput, safeModeActive]);
  const shownModel = model ?? staleModel;
  const isStale = !model && staleModel !== null;
  const staleBanner = isStale && !!monthData.error;
  // A refresh in flight with a table already on screen: the table stays, and a status line says it is updating.
  const refreshing = monthData.loading && !!shownModel && !staleBanner;

  const message: { text: string; alert: boolean; retry: boolean; loading?: boolean } | null = (() => {
    if (months === null && rangeResult.issue) return { text: RANGE_MESSAGES[rangeResult.issue], alert: false, retry: false };
    if (!view.loaded) return { text: "Loading projects…", alert: false, retry: false, loading: true };
    if (monthData.error && !shownModel) return { text: gridLoadErrorCopy(monthData.error), alert: true, retry: true };
    if (view.projectRows.length === 0) return { text: "No projects have figures in this date range. Widen the date range, or clear the project and status filters above.", alert: false, retry: false };
    if (!shownModel) return { text: "Loading months…", alert: false, retry: false, loading: true };
    return null;
  })();

  const toggleColumn = (id: string) => setHidden((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));

  const exportRows = () => (modelInput ? gridExportRecords(modelInput, exportNumber, exportHeader) : []);
  const basename = "profit-pareto";
  const exportToCsv = () => {
    if (safeModeActive) return;
    const data = exportRows();
    if (data.length === 0) return;
    const headers = Object.keys(data[0]);
    const lines = [headers.map((h) => csvCell(h)).join(","), ...data.map((row) => headers.map((h) => csvCell(row[h] ?? "")).join(","))];
    const filename = exportFilename(basename, "csv", new Date());
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
    addToast({ type: "success", title: "CSV downloaded", message: exportConfirmation(filename, data.length - (model?.averages ? 1 : 0)) });
  };
  const exportToXlsx = () => {
    if (safeModeActive) return;
    const data = exportRows().map((row) => {
      const safe: Record<string, string | number> = {};
      for (const [key, value] of Object.entries(row)) safe[key] = xlsxCell(value);
      return safe;
    });
    if (data.length === 0) return;
    const filename = exportFilename(basename, "xlsx", new Date());
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Grid");
    XLSX.writeFile(wb, filename);
    addToast({ type: "success", title: "Spreadsheet downloaded", message: exportConfirmation(filename, data.length - (model?.averages ? 1 : 0)) });
  };

  const toolbar = (
    <div className="pnl-grid-toolbar">
      <div className="pnl-grid-toolbar-spacer" />
      <div className="pnl-grid-menu-wrap" ref={menuRef}>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          ref={columnsButtonRef}
          aria-controls="pnl-grid-column-menu"
          aria-expanded={columnMenuOpen}
          onClick={() => setColumnMenuOpen((open) => !open)}
          disabled={!model || model.rows.length === 0}
        >
          <Settings2 size={14} aria-hidden="true" /> Columns
        </button>
        {columnMenuOpen && model && (
          <div id="pnl-grid-column-menu" className="pnl-grid-column-menu" role="group" aria-label="Show or hide columns">
            {model.columns
              .filter((c) => c.id !== NAME_COLUMN_ID)
              .map((c) => (
                <label key={c.id} className="pnl-grid-column-option">
                  <input type="checkbox" checked={!hidden.includes(c.id)} onChange={() => toggleColumn(c.id)} />
                  {c.group && c.kind !== "derived" ? `${c.group}: ${c.label}` : c.label}
                </label>
              ))}
          </div>
        )}
      </div>
      <div className="pnl-view-switch" role="group" aria-label="Months without costs" title={SHOW_NO_COST_MONTHS_HINT}>
        <button
          type="button"
          aria-pressed={showNoCostMonths}
          onClick={() => {
            const next = !showNoCostMonths;
            setShowNoCostMonths(next);
            saveShowNoCostMonths(next);
          }}
        >
          {SHOW_NO_COST_MONTHS_LABEL}
        </button>
      </div>
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setFullScreen(true)} disabled={!model || model.rows.length === 0}>
        <Maximize2 size={14} aria-hidden="true" /> Full screen
      </button>
      <span className="pnl-export-pair">
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={exportToCsv}
          disabled={safeModeActive || !model || model.rows.length === 0}
          title={safeModeActive ? SAFE_MODE_EXPORT_HINT : "Export CSV"}
        >
          <Download size={14} aria-hidden="true" /> CSV
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={exportToXlsx}
          disabled={safeModeActive || !model || model.rows.length === 0}
          title={safeModeActive ? SAFE_MODE_EXPORT_HINT : "Export XLSX"}
        >
          <Download size={14} aria-hidden="true" /> XLSX
        </button>
      </span>
      {safeModeActive && <span className="pnl-grid-export-note">{SAFE_MODE_EXPORT_HINT}</span>}
    </div>
  );

  const onSort = (c: GridColumn) => model && setSort(nextSort(model.sort, c));

  // SEQ-240: one "How is this worked out" trigger per figure in the header (gridColumnTargets).
  const headerInfo = (columns: readonly GridColumn[]) => {
    const targets = gridColumnTargets(columns);
    return (c: GridColumn) => {
      const target = targets.get(c.id);
      return target ? (
        <HowWorkedOut definition={gridColumnFigure(config, target, { payrollAsAt })} where={`in ${config.name}`} />
      ) : null;
    };
  };

  const body = message?.loading ? (
    <GridSkeleton text={message.text} />
  ) : message ? (
    <div role={message.alert ? "alert" : "status"} className={message.alert ? "pnl-grid-message pnl-grid-message--error" : "pnl-grid-message"}>
      {message.alert && <AlertTriangle size={18} aria-hidden="true" />}
      <p style={{ margin: 0 }}>{message.text}</p>
      {message.retry && (
        <span className="pnl-grid-message-actions">
          <button type="button" className="btn btn-ghost btn-sm" onClick={monthData.refresh}>
            <RefreshCw size={13} aria-hidden="true" /> Retry
          </button>
          {gridErrorKind(monthData.error) === "session" && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => window.location.reload()}>
              Reload the page
            </button>
          )}
        </span>
      )}
    </div>
  ) : shownModel ? (
    <GridTable
      model={shownModel}
      freezeFirst={resolved.freezeFirstColumn}
      onSort={isStale ? undefined : onSort}
      busy={refreshing}
      info={headerInfo(shownModel.visibleColumns)}
    />
  ) : null;

  const banner = staleBanner ? (
    <div role="alert" className="pnl-grid-banner pnl-grid-banner--error">
      <AlertTriangle size={16} aria-hidden="true" />
      <span>{gridRefreshErrorCopy(monthData.error)}</span>
      <button type="button" className="btn btn-ghost btn-sm" onClick={monthData.refresh}>
        <RefreshCw size={13} aria-hidden="true" /> Retry
      </button>
    </div>
  ) : null;

  return (
    <div className="pnl-grid-widget">
      {toolbar}
      {banner}
      {refreshing ? <GridRefreshNote /> : null}
      {body}
      {shownModel && shownModel.rows.length > 0 && <GridLegendAndNotes model={shownModel} extraNote={costCheckFailed ? COSTS_CHECK_FAILED_NOTE : null} />}
      {fullScreen && model && (
        <Modal isOpen onClose={() => setFullScreen(false)} title={config.name} size="full" hideFooter>
          {/* SEQ-240: no triggers inside the full screen dialog: it is a modal with its own focus trap, above the pop-up layer. */}
          <HowWorkedOutVisibility.Provider value={false}>
            <div className="pnl-grid-widget pnl-grid-widget--full">
              <GridTable model={model} freezeFirst={resolved.freezeFirstColumn} onSort={onSort} />
              <GridLegendAndNotes model={model} extraNote={costCheckFailed ? COSTS_CHECK_FAILED_NOTE : null} />
            </div>
          </HowWorkedOutVisibility.Provider>
        </Modal>
      )}
    </div>
  );
}

/** Header groups over consecutive columns with the same kind and group label, and the ids of the first column in each (they carry the left rule). */
function groupSpans(columns: GridColumn[]): { spans: { group: string; span: number; key: string }[]; starts: Set<string> } {
  const spans: { group: string; span: number; key: string }[] = [];
  const starts = new Set<string>();
  let previous = "";
  for (const c of columns) {
    if (c.id === NAME_COLUMN_ID) continue;
    const key = `${c.kind}:${c.group}`;
    if (key === previous) spans[spans.length - 1].span += 1;
    else {
      spans.push({ group: c.group, span: 1, key });
      starts.add(c.id);
    }
    previous = key;
  }
  return { spans, starts };
}

function GridTable({
  model,
  freezeFirst,
  onSort,
  busy = false,
  info,
}: {
  model: GridModel;
  freezeFirst: boolean;
  onSort?: (column: GridColumn) => void;
  busy?: boolean;
  /** SEQ-240: the header trigger for a column, or null for a column without one. */
  info?: (column: GridColumn) => ReactNode;
}) {
  const columns = model.visibleColumns;
  const { spans, starts } = groupSpans(columns);
  const dataColumns = columns.filter((c) => c.id !== NAME_COLUMN_ID);
  const nameColumn = columns.find((c) => c.id === NAME_COLUMN_ID);
  const stickyClass = freezeFirst ? "pnl-sticky-col" : undefined;

  const ariaSort = (c: GridColumn): "ascending" | "descending" | "none" =>
    model.sort.column === c.id ? (model.sort.direction === "asc" ? "ascending" : "descending") : "none";

  const sortButton = (c: GridColumn) => {
    const active = model.sort.column === c.id;
    return (
      <button type="button" className="pnl-table-sort pnl-grid-sort" aria-label={`Sort by ${c.group ? `${c.group} ` : ""}${c.label}`} onClick={() => onSort?.(c)} disabled={!onSort}>
        {c.label}
        {active ? (model.sort.direction === "asc" ? <ArrowUp size={12} aria-hidden="true" /> : <ArrowDown size={12} aria-hidden="true" />) : <ArrowUpDown size={12} aria-hidden="true" className="pnl-grid-sort-idle" />}
      </button>
    );
  };

  const renderRow = (row: GridRow, kind: "body" | "average") => (
    <tr key={row.id} className={kind === "average" ? "pnl-month-summary-row" : "pnl-table-row"}>
      <th scope="row" className={`${stickyClass ?? ""} pnl-month-project-cell`.trim()}>
        {kind === "body" && !row.isOther ? (
          <Link className="pnl-table-link pnl-month-project-link" to={`/projects/${row.id}?tab=status`} title={row.status ? `${row.label} (${row.status})` : row.label}>
            {row.label}
          </Link>
        ) : (
          row.label
        )}
      </th>
      {dataColumns.map((c) => {
        const cell = row.cells[c.id];
        return (
          <td
            key={c.id}
            data-seq-private=""
            data-align={c.align}
            data-band-colour={cell?.band?.colour}
            className={starts.has(c.id) ? "pnl-month-group-start" : undefined}
          >
            {cell?.band && <span aria-hidden="true" className="pnl-grid-marker">{cell.band.marker}</span>}
            {cell?.text ? cell.text : c.unit !== "text" && !cell?.suffix ? (<><span className="pnl-grid-nodata" aria-hidden="true">-</span><span className="pnl-visually-hidden">No data</span></>) : ""}
            {cell?.suffix && <span className="pnl-month-nocost pnl-month-nocost--inline">{cell.suffix}</span>}
            {cell?.band && <span className="pnl-visually-hidden">, {cell.band.label}</span>}
          </td>
        );
      })}
    </tr>
  );

  return (
    <div className="pnl-table-scroll pnl-table-scroll--months pnl-chart-body pnl-grid-scroll" tabIndex={0} role="region" aria-label="Grid table, scrolls" aria-busy={busy}>
      <table className="pnl-grid-table">
        <thead>
          <tr>
            <th rowSpan={2} scope="col" className={stickyClass} aria-sort={nameColumn ? ariaSort(nameColumn) : "none"} data-align="left">
              <span className="hwo-heading">
                {nameColumn ? sortButton(nameColumn) : "Project"}
                {nameColumn && info?.(nameColumn)}
              </span>
            </th>
            {spans.map((s) => (
              <th key={s.key} colSpan={s.span} scope="colgroup" className="pnl-month-group-start pnl-grid-group">
                {s.group}
              </th>
            ))}
          </tr>
          <tr>
            {dataColumns.map((c) => (
              <th key={c.id} scope="col" aria-sort={ariaSort(c)} data-align={c.align} className={starts.has(c.id) ? "pnl-month-group-start" : undefined}>
                <span className="hwo-heading">
                  {sortButton(c)}
                  {info?.(c)}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {model.averages && renderRow(model.averages, "average")}
          {model.rows.map((row) => renderRow(row, "body"))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * A skeleton of the real layout: a header band, then rows with a narrow first
 * cell. The words are always in a live region for assistive technology and,
 * after two seconds, show as a visible muted line too (standard 6.1).
 */
/** A refresh while a table is on screen: the table stays and this says so, with the narrow-the-range hint after two seconds. */
function GridRefreshNote() {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const handle = setTimeout(() => setSlow(true), 2000);
    return () => clearTimeout(handle);
  }, []);
  return (
    <p role="status" className="pnl-grid-refresh-note">
      Refreshing the monthly figures…{slow ? " Long date ranges take longer: narrow the date range above to load it faster." : ""}
    </p>
  );
}

function GridSkeleton({ text }: { text: string }) {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const handle = setTimeout(() => setSlow(true), 2000);
    return () => clearTimeout(handle);
  }, []);
  return (
    <div className="pnl-grid-skeleton" role="status" aria-busy="true">
      <div className="pnl-grid-skeleton-row" aria-hidden="true">
        <div className="skeleton pnl-grid-skeleton-first" />
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="skeleton" />
        ))}
      </div>
      {Array.from({ length: 5 }, (_, r) => (
        <div key={r} className="pnl-grid-skeleton-row" aria-hidden="true">
          <div className="skeleton pnl-grid-skeleton-first" />
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="skeleton" />
          ))}
        </div>
      ))}
      <span className={slow ? "pnl-grid-skeleton-text" : "pnl-visually-hidden"}>{slow ? `${text} Long date ranges take longer: narrow the date range above to load it faster.` : text}</span>
    </div>
  );
}

function GridLegendAndNotes({ model, extraNote }: { model: GridModel; extraNote: string | null }) {
  return (
    <div className="pnl-grid-footer">
      {model.legend.map((entry) => (
        <div key={entry.measureLabel} className="pnl-grid-legend" role="group" aria-label={`Colour key for ${entry.measureLabel}`}>
          <span className="pnl-grid-legend-title">{entry.measureLabel}</span>
          {entry.bands.map((band) => (
            <span key={band.label} className="pnl-grid-legend-item" data-band-colour={band.colour}>
              <span aria-hidden="true" className="pnl-grid-marker">{band.marker}</span>
              {band.label}: {band.range}
            </span>
          ))}
        </div>
      ))}
      {[...model.notes, ...(extraNote ? [extraNote] : [])].map((note) => (
        <p key={note} className="pnl-month-note">
          {note}
        </p>
      ))}
    </div>
  );
}
